import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';
import { User, Session } from '@supabase/supabase-js';
import { api } from '../../services/api';

// Define a more detailed user type including profile data
interface AppUser extends User {
  username?: string;
  profile_image_url?: string | null;
  // Add other profile fields if needed
}

interface AuthState {
  user: AppUser | null; // Use the extended type
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  session: null,
  isAdmin: false,
  loading: true,
  error: null,
};

// Helper function to fetch profile data
const fetchUserProfile = async (userId: string): Promise<Partial<AppUser>> => {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('username, profile_image_url') // Select only needed fields
      .eq('id', userId)
      .single();

    if (error && error.code !== 'PGRST116') { // Ignore 'PGRST116' (No rows found)
      console.error('Error fetching user profile in authSlice:', error);
      return {}; // Return empty object on error
    }
    return profile || {}; // Return profile data or empty object if not found
  } catch (err) {
    console.error('Exception fetching user profile:', err);
    return {};
  }
};

// Async thunks
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const [hasAdminAccess, userProfile] = await Promise.all([
          api.isAdmin(),
          fetchUserProfile(session.user.id)
        ]);
        // Merge Supabase user with profile data
        const userWithProfile = { ...session.user, ...userProfile };
        return { session, isAdmin: hasAdminAccess, user: userWithProfile };
      }
      return { session: null, isAdmin: false, user: null };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Initialization failed');
    }
  }
);

export const signUp = createAsyncThunk(
  'auth/signUp',
  async ({ email, password, username }: { email: string; password: string; username: string }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { username } // Store username in metadata AND in profiles table
        }
      });
      
      if (error) throw error; // Let the existing error handling catch issues

      // IMPORTANT: After successful Supabase signUp, create the profile entry
      if (data.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({ 
            id: data.user.id, 
            username: username.toLowerCase(), // Store username consistently
            created_at: new Date().toISOString(),
            profile_image_status: 'none' // Default status
            // Add other default profile fields if necessary
          });

        if (profileError) {
          console.error('Error creating profile after signup:', profileError);
          // Even if profile creation fails, continue login, but maybe log or alert?
        }
      }

      // Return basic Supabase data, profile will be fetched on next init/signIn
      return data; 
    } catch (err: any) {
       // Use existing detailed error handling
       const errorMsg = err.message || '';
       let status = err.status || 500;
       let message = 'Registration failed. Please try again.';

        if (errorMsg.includes('Database error saving new user') || 
            status === 500 || 
            errorMsg.includes('unique constraint') || 
            errorMsg.includes('duplicate key value violates unique constraint \"profiles_username_key\"')) { // Specific check
          message = 'This username is already taken.';
          status = 409;
        } else if (errorMsg.includes('Password should be at least 6 characters')) {
            message = 'Password must be at least 6 characters.'; // Sync with Supabase default
            status = 422;
        } else if (errorMsg.includes('User already registered') || (errorMsg.includes('email') && errorMsg.includes('already'))) {
            message = 'This email is already registered.';
            status = 409;
        } else {
            message = errorMsg || message; // Use Supabase error if available
        }
        
        return rejectWithValue({ message, status });
    }
  }
);

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        console.log('Supabase auth error:', error);
        // Format error message for better user display
        if (error.message.includes('Invalid login credentials')) {
          return rejectWithValue('Invalid email or password. Please try again.');
        }
        throw error;
      }
      
      if (data?.user) {
          const [hasAdminAccess, userProfile] = await Promise.all([
            api.isAdmin(),
            fetchUserProfile(data.user.id)
          ]);
          // Merge Supabase user with profile data
          const userWithProfile = { ...data.user, ...userProfile };
          return { ...data, isAdmin: hasAdminAccess, user: userWithProfile };
      }
      // Should not happen if signIn succeeds, but handle defensively
      return { ...data, isAdmin: false, user: null }; 
    } catch (error: any) {
      console.log('Sign in error:', error);
      return rejectWithValue(error.message || 'Failed to sign in. Please try again.');
    }
  }
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async () => {
    try {
      // First, properly sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Error signing out:', error);
        // Even if there's an error, continue with cleanup below
      }
      
      // Clear auth-related storage only, not all storage
      // This preserves cookie consent and other user preferences
      
      return null;
    } catch (err) {
      console.error('Exception during sign out:', err);
      return null;
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setSession: (state, action) => {
      state.session = action.payload;
    },
    setAdmin: (state, action) => {
      state.isAdmin = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.session = null;
      state.isAdmin = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.session = action.payload.session;
        state.user = action.payload.user; // Now includes profile data
        state.isAdmin = action.payload.isAdmin;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string ?? 'Failed to initialize auth';
      })
      // Sign Up - Note: Doesn't set user state immediately, relies on initializeAuth/signIn
      .addCase(signUp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        // Don't set user/session here - user needs email confirmation
        // state.session = action.payload.session; 
        // state.user = action.payload.user; // Basic user only
      })
      .addCase(signUp.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.error = (action.payload as any).message ?? 'Failed to sign up';
        } else {
          state.error = action.error.message ?? 'Failed to sign up';
        }
      })
      // Sign In
      .addCase(signIn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.session = action.payload.session;
        state.user = action.payload.user; // Now includes profile data
        state.isAdmin = action.payload.isAdmin;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string ?? 'Failed to sign in';
      })
      // Sign Out
      .addCase(signOut.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.session = null;
        state.isAdmin = false;
      })
      .addCase(signOut.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to sign out';
      });
  },
});

export const { setUser, setSession, setAdmin, clearAuth } = authSlice.actions;
export default authSlice.reducer;